import { useEffect, useState, useRef, useMemo } from 'react';
import { css } from '@emotion/react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { motion, AnimatePresence, Variants } from 'motion/react';
import { useAiScheduleAccess } from '@frontend/ai-receptionist-settings';
import { getTodaysDate } from '@frontend/date';
import { useTranslation } from '@frontend/i18n';
import { useCalendarGridAccess } from '@frontend/schedule-helpers';
import { InsuranceEligibilityCalendarCallout } from '@frontend/schedule-pulse-insurance-tab';
import { useAppScopeStore } from '@frontend/scope';
import { useSearch } from '@frontend/weave-router';
import { ContentLoader } from '@frontend/design-system';
import { CalendarLocationSelectorModal } from '../../components/calendar-location-selector-modal';
import { ColumnEntityReorderActions } from '../../components/calendar-view/calendar-column-entity-list-section/column-entity-reorder-actions';
import {
  ColumnEntityReorderStoreProvider,
  useColumnEntityReorderStore,
} from '../../components/calendar-view/providers';
import { MAX_CALENDAR_LOCATION_COUNT } from '../../constants';
import { useCurrentDateQueryParam } from '../../hooks/scheduler-v3/use-current-date-query-param';
import { useGetCalendarV2QueriesData } from '../../hooks/use-get-calendar-v2-queries-data';
import { useIsScheduleSmallModeWindow } from '../../hooks/use-schedule-small-mode';
import { useSyncCalendarLocation } from '../../stores/calendar-location.store';
import { CalendarProvider, useCalendarStore } from '../../stores/calendar.store';
import { CalendarHeader } from '../CalendarV3/components/header';
import { SchedulePulseTrayContent } from '../schedule-pulse-panel';
import { CalendarTimeLineViewV2, CalendarTimeLineViewV2Props } from './CalendarTimeLineViewV2';
import { CalendarV2WithGrid } from './CalendarV2WithGrid';

dayjs.extend(utc);

const CalendarV2Component = () => {
  const { t } = useTranslation('schedule');

  // Sync the currentDate query param with the calendarDate in the store
  useCurrentDateQueryParam();

  const { selectedLocationIds } = useAppScopeStore();

  const [selectedMultiLocationIds, setSelectedMultiLocationIds] = useState<string[]>(selectedLocationIds ?? []);
  const performedSearch = useRef(false);

  const setDefaultSelections = (locationIds: string[]) => {
    setSelectedMultiLocationIds(locationIds);
  };

  const searchParams = useSearch({ from: '/schedule/calendar' });

  const { calendarDate } = useCalendarStore(['calendarDate']);
  const {
    filteredAppointments,
    filteredProviderList,
    calendarCounts,
    providersOfficeHours,
    providerExceptions,
    officeHours,
    officeExceptions,
    isLoading,
  } = useGetCalendarV2QueriesData();
  const { isInReorderMode } = useColumnEntityReorderStore(['isInReorderMode']);

  const { hasAiReceptionistAccess } = useAiScheduleAccess();
  const { isCalendarGridEnabled } = useCalendarGridAccess();

  const hideAvailability = hasAiReceptionistAccess;

  useEffect(() => {
    const searchAppointment = () => {
      const element = document.querySelector(`[data-event-id="${searchParams.appointmentId}"]`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
        (element as HTMLElement).click();
        performedSearch.current = true;
      }
    };

    if (
      !searchParams.appointmentId ||
      searchParams.currentDate !== calendarDate ||
      isLoading ||
      performedSearch.current
    ) {
      return;
    }
    if (selectedMultiLocationIds.length > MAX_CALENDAR_LOCATION_COUNT) return;

    const timeoutId = setTimeout(searchAppointment, 100);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [searchParams?.appointmentId, searchParams.currentDate, isLoading, calendarDate, selectedMultiLocationIds.length]);

  const calendarComponentProps = useMemo<CalendarTimeLineViewV2Props>(
    () => ({
      filteredAppointments,
      filteredProviderList,
      providerExceptions: hideAvailability ? [] : providerExceptions,
      providersOfficeHours: hideAvailability ? [] : providersOfficeHours,
      officeHours: hideAvailability ? [] : officeHours,
      officeExceptions: hideAvailability ? [] : officeExceptions,
      isLoading,
    }),
    [
      filteredAppointments,
      filteredProviderList,
      hideAvailability,
      isLoading,
      officeExceptions,
      officeHours,
      providerExceptions,
      providersOfficeHours,
    ]
  );
  return (
    <>
      <section css={pageStyle} id='calendar-view-wrapper'>
        <InsuranceEligibilityCalendarCallout />
        {isInReorderMode ? (
          <ColumnEntityReorderActions />
        ) : (
          <CalendarHeader
            calendarEventsCount={calendarCounts.total}
            unconfirmedCalendarEventsCount={calendarCounts.unconfirmed}
            insuranceAlertCalendarEventsCount={calendarCounts.insurance}
            calendarTodayDate={getTodaysDate('MM/DD/YYYY')}
          />
        )}
        <div css={{ position: 'relative', overflow: 'auto', flex: 1 }}>
          <ContentLoader
            show={isLoading}
            message={t('Loading appointments...')}
            css={{ justifyContent: 'flex-start', paddingTop: 'calc(50vh - 150px)' }}
          />
          {isCalendarGridEnabled ? (
            <CalendarV2WithGrid {...calendarComponentProps} />
          ) : (
            <CalendarTimeLineViewV2 {...calendarComponentProps} />
          )}
        </div>
      </section>
      <CalendarLocationSelectorModal onSelect={setDefaultSelections} />
    </>
  );
};

const fadeVariants: Variants = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
};

const CalendarV2Wrapper = () => {
  const { isSmallModeWindow } = useIsScheduleSmallModeWindow();

  useSyncCalendarLocation();

  return (
    <>
      <AnimatePresence mode='wait'>
        {isSmallModeWindow ? (
          <motion.div
            key='list-view'
            variants={fadeVariants}
            initial='initial'
            animate='animate'
            exit='exit'
            css={{ height: '100%' }}
          >
            <SchedulePulseTrayContent isSmallModeWindow />
          </motion.div>
        ) : (
          <motion.div
            key='calendar-view'
            variants={fadeVariants}
            initial='initial'
            animate='animate'
            exit='exit'
            css={{ height: '100%' }}
          >
            <ColumnEntityReorderStoreProvider>
              <CalendarV2Component />
            </ColumnEntityReorderStoreProvider>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export const CalendarV2 = () => (
  <CalendarProvider>
    <CalendarV2Wrapper />
  </CalendarProvider>
);

const pageStyle = css({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  position: 'relative',
  containerType: 'inline-size',
});
